import React, { useEffect, useState } from 'react';
import { Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select } from 'antd';
import dayjs from 'libs/dayjs';
import CatalogoItemPicker from 'components/admin/Hallazgos/CatalogoItemPicker';
import EquipoAscensorSelect from './EquipoAscensorSelect';

/**
 * Alta de una tarea de la ODT desde la carta Gantt.
 *
 * Pide todos los campos —no solo la descripcion— porque la tabla de la carta
 * solo edita responsable y fechas en linea: si el equipo o el material no se
 * pudieran cargar aca, habria que salir a la ficha de la ODT igual, que es
 * justo lo que esta pantalla evita.
 *
 * Props:
 *   open
 *   padre — tarea padre si es subtarea; null si es de primer nivel.
 *   idCliente — cliente de la ODT (para listar sus equipos).
 *   especialistas — [{id, fullname}] para el responsable.
 *   guardando — deshabilita el boton mientras se persiste.
 *   onCrear(payload) — payload con la forma del body de POST /orders/:id/items.
 *   onCancelar()
 */
export default function NuevaTareaModal({
  open,
  padre = null,
  idCliente = null,
  especialistas = [],
  guardando = false,
  onCrear,
  onCancelar,
}) {
  const [form] = Form.useForm();
  const esSubtarea = Boolean(padre);
  // El material va por estado local y NO por Form.Item con `name`: CatalogoItemPicker
  // emite el ITEM COMPLETO por onChange (no un id). Si se envolviera en un Form.Item
  // con `name`, rc-field-form igual llama al onChange que le pasamos, pero es el
  // store del formulario el que queda con el objeto entero en vez del id —y eso es
  // lo que se manda al backend.
  const [idMaterial, setIdMaterial] = useState(null);

  // Al abrir: limpia y, si es subtarea, precarga el rango del padre. Es lo mas
  // probable y queda editable. Una tarea de primer nivel arranca sin fechas
  // porque no hay de donde deducirlas.
  //
  // Depende de primitivos de `padre` (no del objeto) para no volver a correr —y
  // pisar lo que el usuario ya escribio con resetFields— si quien lo consume
  // reconstruye el objeto `padre` en cada render.
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setIdMaterial(null);
    if (padre?.planned_start || padre?.planned_end) {
      form.setFieldsValue({
        planned_start: padre.planned_start ? dayjs(padre.planned_start) : null,
        planned_end: padre.planned_end ? dayjs(padre.planned_end) : null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, padre?.id, padre?.planned_start, padre?.planned_end, form]);

  const confirmar = async () => {
    let v;
    try {
      v = await form.validateFields();
    } catch {
      // Campos invalidos: antd ya muestra el error debajo de cada uno.
      return;
    }
    onCrear?.({
      description: (v.description || '').trim(),
      parent_id: padre?.id ?? null,
      planned_start: v.planned_start ? v.planned_start.format('YYYY-MM-DD') : null,
      planned_end: v.planned_end ? v.planned_end.format('YYYY-MM-DD') : null,
      assigned_to_user_id: v.assigned_to_user_id ?? null,
      id_ascensor: v.id_ascensor ?? null,
      id_coleccion_item: idMaterial,
      cantidad: v.cantidad ?? null,
    });
  };

  return (
    <Modal
      open={open}
      title={esSubtarea ? `Nueva subtarea de «${padre.description || 'la tarea'}»` : 'Nueva tarea'}
      okText="Crear"
      cancelText="Cancelar"
      confirmLoading={guardando}
      onOk={confirmar}
      onCancel={onCancelar}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="description"
          label="Descripción"
          rules={[{ required: true, message: 'Escribe una descripción para la tarea' }]}
        >
          <Input placeholder="Por ejemplo: cambio de cables de tracción" />
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="id_ascensor" label="Equipo">
              <EquipoAscensorSelect idCliente={idCliente} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="assigned_to_user_id" label="Responsable">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Sin asignar"
                options={especialistas.map((u) => ({ value: u.id, label: u.fullname }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="planned_start" label="Inicio">
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="planned_end" label="Fin">
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            {/* Sin `name`: el material se maneja por estado local (ver comentario arriba). */}
            <Form.Item label="Material">
              <CatalogoItemPicker
                size="middle"
                value={idMaterial}
                onChange={(item) => setIdMaterial(item?.id ?? null)}
                onClear={() => setIdMaterial(null)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="cantidad" label="Cantidad">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
